import React, { useState, useEffect, useCallback, useRef } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";

const TEMPLATE_DEFS = [
  {
    key: "registro_recibido",
    name: "Registro recibido",
    toggleKey: "notify_user_registered",
    vars: ["{{empresa}}", "{{nombre_usuario}}", "{{anio}}", "{{razon_social}}", "{{nit}}", "{{email}}", "{{fecha}}"],
  },
  {
    key: "cuenta_activada",
    name: "Cuenta activada",
    toggleKey: "notify_user_activated",
    vars: ["{{empresa}}", "{{nombre_usuario}}", "{{anio}}", "{{razon_social}}", "{{nit}}", "{{email}}", "{{login_url}}"],
  },
  {
    key: "cuenta_rechazada",
    name: "Cuenta rechazada",
    toggleKey: "notify_user_rejected",
    vars: ["{{empresa}}", "{{nombre_usuario}}", "{{anio}}", "{{razon_social}}", "{{nit}}", "{{email}}", "{{fecha}}"],
  },
  {
    key: "actualizacion_asignada",
    name: "Nueva actualizaci\u00f3n asignada",
    toggleKey: "notify_update_assigned",
    vars: ["{{empresa}}", "{{nombre_usuario}}", "{{anio}}", "{{nombre_actualizacion}}", "{{version}}", "{{ttl_horas}}", "{{dashboard_url}}", "{{razon_social}}"],
  },
  {
    key: "url_por_vencer",
    name: "URL pr\u00f3xima a vencer",
    toggleKey: "notify_url_expiring",
    vars: ["{{empresa}}", "{{nombre_usuario}}", "{{anio}}", "{{nombre_actualizacion}}", "{{fecha_vencimiento}}", "{{dashboard_url}}"],
  },
  {
    key: "limite_descargas",
    name: "L\u00edmite de descargas alcanzado",
    toggleKey: "notify_download_limit_reached",
    vars: ["{{empresa}}", "{{nombre_usuario}}", "{{anio}}", "{{nombre_actualizacion}}", "{{descargas_realizadas}}", "{{limite_descargas}}", "{{dashboard_url}}"],
  },
  {
    key: "restablecer_contrasena",
    name: "Restablecimiento de contrase\u00f1a",
    toggleKey: "notify_password_reset",
    vars: ["{{empresa}}", "{{nombre_usuario}}", "{{anio}}", "{{reset_url}}"],
  },
];

const SAMPLE_VARS = {
  empresa: "Update-vital-Ftech",
  nombre_usuario: "Carlos Rodr\u00edguez",
  anio: new Date()?.getFullYear()?.toString(),
  razon_social: "Tecnolog\u00eda Avanzada S.A.S.",
  nit: "900.123.456-7",
  email: "carlos.rodriguez@tecavanzada.com.co",
  fecha: "6 de marzo de 2026",
  login_url: "https://updatehub7976.builtwithrocket.new/login",
  nombre_actualizacion: "Sistema de Gesti\u00f3n Empresarial Pro",
  version: "4.2.1",
  ttl_horas: "24",
  dashboard_url: "https://updatehub7976.builtwithrocket.new/customer-user-dashboard",
  fecha_vencimiento: "8 de marzo de 2026 a las 10:00 COT",
  descargas_realizadas: "5",
  limite_descargas: "5",
  reset_url: "https://updatehub7976.builtwithrocket.new/login?reset=true",
};

function substituteVars(text, vars) {
  if (!text) return "";
  return text?.replace(/\{\{(\w+)\}\}/g, (match, key) => vars?.[key] ?? match);
}

function CodeEditor({ value, onChange, placeholder, rows = 18 }) {
  const textareaRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e?.key === "Tab") {
      e?.preventDefault();
      const start = e?.target?.selectionStart;
      const end = e?.target?.selectionEnd;
      const newVal = value?.substring(0, start) + "  " + value?.substring(end);
      onChange(newVal);
      setTimeout(() => {
        if (textareaRef?.current) {
          textareaRef.current.selectionStart = start + 2;
          textareaRef.current.selectionEnd = start + 2;
        }
      }, 0);
    }
  };

  return (
    <div className="relative rounded-lg overflow-hidden border" style={{ borderColor: "var(--color-border)" }}>
      <div
        className="flex items-center gap-2 px-3 py-2 border-b text-xs font-caption"
        style={{ backgroundColor: "var(--color-muted)", borderColor: "var(--color-border)", color: "var(--color-muted-foreground)" }}
      >
        <Icon name="Code" size={13} color="var(--color-muted-foreground)" />
        HTML Body
        <span className="ml-auto">Tab = 2 espacios</span>
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e?.target?.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        spellCheck={false}
        className="w-full px-4 py-3 text-xs focus:outline-none resize-y"
        style={{
          fontFamily: "'Courier New', Courier, monospace",
          backgroundColor: "var(--color-background)",
          color: "var(--color-foreground)",
          lineHeight: "1.6",
          minHeight: "200px",
        }}
      />
    </div>
  );
}

export default function EmailTemplatesConfig({ onToast }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState({});
  const [toggles, setToggles] = useState({});
  const [selectedKey, setSelectedKey] = useState(TEMPLATE_DEFS?.[0]?.key);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [showPlainText, setShowPlainText] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const selectedDef = TEMPLATE_DEFS?.find((d) => d?.key === selectedKey);
  const currentTemplate = templates?.[selectedKey] || {};

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data: tplData, error: tplError } = await supabase
        ?.from("email_templates")
        ?.select("*");
      if (tplError) throw tplError;

      const { data: settingsData } = await supabase
        ?.from("system_settings")
        ?.select("key, value_json")
        ?.eq("key", "notification_toggles")
        ?.single();

      const tplMap = {};
      (tplData || [])?.forEach((t) => {
        tplMap[t.template_key] = t;
      });
      setTemplates(tplMap);
      setToggles(settingsData?.value_json || {});
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleFieldChange = (field, value) => {
    setTemplates((prev) => ({
      ...prev,
      [selectedKey]: { ...prev?.[selectedKey], [field]: value },
    }));
    setHasChanges(true);
  };

  const handleToggleActive = async (isActive) => {
    handleFieldChange("is_active", isActive);
    const newToggles = { ...toggles, [selectedDef?.toggleKey]: isActive };
    setToggles(newToggles);
    try {
      await supabase
        ?.from("system_settings")
        ?.upsert({ key: "notification_toggles", value: null, value_json: newToggles }, { onConflict: "key" });
    } catch (err) {
    }
  };

  const insertVar = (varStr) => {
    const activeEl = document.activeElement;
    const field = activeEl?.dataset?.field;
    if (field && (field === "subject" || field === "html_body" || field === "plain_text" || field === "preview_text")) {
      const start = activeEl?.selectionStart ?? (activeEl?.value || "")?.length;
      const end = activeEl?.selectionEnd ?? start;
      const current = currentTemplate?.[field] || "";
      const newVal = current?.substring(0, start) + varStr + current?.substring(end);
      handleFieldChange(field, newVal);
      setTimeout(() => {
        activeEl?.focus();
        activeEl.selectionStart = start + varStr?.length;
        activeEl.selectionEnd = start + varStr?.length;
      }, 0);
    } else {
      handleFieldChange("html_body", (currentTemplate?.html_body || "") + varStr);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const tpl = templates?.[selectedKey];
      const payload = {
        template_key: selectedKey,
        name: tpl?.name || selectedDef?.name,
        subject: tpl?.subject || "",
        preview_text: tpl?.preview_text || null,
        html_body: tpl?.html_body || "",
        plain_text: tpl?.plain_text || null,
        is_active: tpl?.is_active ?? true,
        updated_by: user?.id || null,
        updated_at: new Date()?.toISOString(),
      };
      const { error } = await supabase
        ?.from("email_templates")
        ?.upsert(payload, { onConflict: "template_key" });
      if (error) throw error;
      setHasChanges(false);
      onToast?.("Plantilla guardada exitosamente", "success");
    } catch (err) {
      onToast?.("Error al guardar la plantilla", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetConfirm = async () => {
    setShowResetConfirm(false);
    setSaving(true);
    try {
      const { error } = await supabase
        ?.from("email_templates")
        ?.delete()
        ?.eq("template_key", selectedKey);
      if (error) throw error;
      await fetchTemplates();
      setHasChanges(false);
      onToast?.("Plantilla restaurada al valor predeterminado", "success");
    } catch (err) {
      onToast?.("Error al restaurar la plantilla", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 0.8s linear infinite" }}>
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      </div>
    );
  }

  const previewHtml = substituteVars(currentTemplate?.html_body || "", SAMPLE_VARS);
  const isActive = currentTemplate?.is_active ?? true;
  const updatedAt = currentTemplate?.updated_at
    ? new Date(currentTemplate.updated_at)?.toLocaleString("es-CO")
    : null;

  return (
    <div className="space-y-4">
      {hasChanges && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-caption" style={{ backgroundColor: "rgba(217,119,6,0.1)", color: "var(--color-warning)" }}>
          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          Tienes cambios sin guardar en esta plantilla
        </div>
      )}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left: Template list */}
        <div className="w-full lg:w-64 xl:w-72 flex-shrink-0">
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-muted)" }}>
              <p className="text-xs font-caption font-600 uppercase tracking-wide" style={{ color: "var(--color-muted-foreground)" }}>Plantillas</p>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--color-border)" }}>
              {TEMPLATE_DEFS?.map((def) => {
                const tpl = templates?.[def?.key];
                const active = tpl?.is_active ?? true;
                const isSelected = selectedKey === def?.key;
                return (
                  <button
                    key={def?.key}
                    onClick={() => { setSelectedKey(def?.key); setHasChanges(false); }}
                    className="w-full flex items-start gap-3 px-4 py-3 text-left transition-base"
                    style={{
                      backgroundColor: isSelected ? "rgba(15,118,110,0.08)" : "transparent",
                      borderLeft: isSelected ? "3px solid var(--color-primary)" : "3px solid transparent",
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-caption font-500 truncate" style={{ color: isSelected ? "var(--color-primary)" : "var(--color-foreground)" }}>
                        {def?.name}
                      </p>
                      <span
                        className="inline-flex items-center gap-1 mt-1 text-xs font-caption px-1.5 py-0.5 rounded-full"
                        style={{
                          backgroundColor: active ? "rgba(15,118,110,0.1)" : "rgba(100,116,139,0.1)",
                          color: active ? "var(--color-primary)" : "var(--color-muted-foreground)",
                        }}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"}`} />
                        {active ? "Activa" : "Inactiva"}
                      </span>
                    </div>
                    {isSelected && <Icon name="ChevronRight" size={14} color="var(--color-primary)" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: Editor */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Header */}
          <div className="rounded-xl border p-4" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(15,118,110,0.1)" }}>
                  <Icon name="Mail" size={18} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="font-heading font-700 text-base" style={{ color: "var(--color-foreground)" }}>{selectedDef?.name}</h3>
                  {updatedAt && (
                    <p className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>Guardado: {updatedAt}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>{isActive ? "Activa" : "Inactiva"}</span>
                  <button
                    onClick={() => handleToggleActive(!isActive)}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                    style={{ backgroundColor: isActive ? "var(--color-primary)" : "var(--color-border)" }}
                    role="switch"
                    aria-checked={isActive}
                  >
                    <span
                      className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                      style={{ transform: isActive ? "translateX(24px)" : "translateX(4px)" }}
                    />
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={() => setShowResetConfirm(true)}
                >
                  Restaurar predeterminado
                </Button>
                <Button
                  variant={showPreview ? "default" : "outline"}
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                  onClick={() => setShowPreview((v) => !v)}
                >
                  Vista Previa
                </Button>
              </div>
            </div>
          </div>

          {/* Fields */}
          <div className="rounded-xl border p-4 md:p-5 space-y-4" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <div>
              <label className="block text-sm font-caption font-500 mb-1.5" style={{ color: "var(--color-foreground)" }}>Asunto del correo</label>
              <input
                data-field="subject"
                type="text"
                value={currentTemplate?.subject || ""}
                onChange={(e) => handleFieldChange("subject", e?.target?.value)}
                className="w-full rounded-lg border px-3 py-2.5 text-sm font-caption focus:outline-none focus:ring-2"
                style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-background)", color: "var(--color-foreground)" }}
                placeholder="Asunto del correo electr\u00f3nico"
              />
            </div>

            <div>
              <label className="block text-sm font-caption font-500 mb-1.5" style={{ color: "var(--color-foreground)" }}>
                Texto de vista previa <span className="font-400" style={{ color: "var(--color-muted-foreground)" }}>(opcional)</span>
              </label>
              <input
                data-field="preview_text"
                type="text"
                value={currentTemplate?.preview_text || ""}
                onChange={(e) => handleFieldChange("preview_text", e?.target?.value)}
                className="w-full rounded-lg border px-3 py-2.5 text-sm font-caption focus:outline-none focus:ring-2"
                style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-background)", color: "var(--color-foreground)" }}
                placeholder="Texto corto visible en la bandeja de entrada antes de abrir el correo"
              />
            </div>

            <div>
              <label className="block text-sm font-caption font-500 mb-1.5" style={{ color: "var(--color-foreground)" }}>Cuerpo HTML</label>
              <div data-field="html_body">
                <CodeEditor
                  value={currentTemplate?.html_body || ""}
                  onChange={(v) => handleFieldChange("html_body", v)}
                  placeholder="<div>Escribe el HTML del correo aqu\u00ed...</div>"
                  rows={18}
                />
              </div>
            </div>

            <div>
              <button
                onClick={() => setShowPlainText((v) => !v)}
                className="flex items-center gap-2 text-sm font-caption font-500 mb-2"
                style={{ color: "var(--color-muted-foreground)" }}
              >
                <Icon name={showPlainText ? "ChevronDown" : "ChevronRight"} size={14} color="var(--color-muted-foreground)" />
                Texto plano (fallback)
              </button>
              {showPlainText && (
                <textarea
                  data-field="plain_text"
                  value={currentTemplate?.plain_text || ""}
                  onChange={(e) => handleFieldChange("plain_text", e?.target?.value)}
                  rows={5}
                  className="w-full rounded-lg border px-3 py-2.5 text-sm font-caption focus:outline-none focus:ring-2 resize-y"
                  style={{
                    borderColor: "var(--color-border)",
                    backgroundColor: "var(--color-background)",
                    color: "var(--color-foreground)",
                    fontFamily: "'Courier New', Courier, monospace",
                  }}
                  placeholder="Versi\u00f3n de texto plano del correo (para clientes que no soportan HTML)"
                />
              )}
            </div>
          </div>

          {/* Variable chips */}
          <div className="rounded-xl border p-4" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Braces" size={15} color="var(--color-secondary)" />
              <p className="text-sm font-caption font-600" style={{ color: "var(--color-foreground)" }}>Variables disponibles</p>
              <span className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>— haz clic para insertar en el cursor</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedDef?.vars?.map((v) => (
                <button
                  key={v}
                  onClick={() => insertVar(v)}
                  className="px-2.5 py-1 rounded-md text-xs font-caption font-500 border transition-base hover:opacity-80"
                  style={{
                    backgroundColor: "rgba(8,145,178,0.08)",
                    borderColor: "rgba(8,145,178,0.25)",
                    color: "var(--color-secondary)",
                    fontFamily: "'Courier New', Courier, monospace",
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Live Preview */}
          {showPreview && (
            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-muted)" }}>
                <div className="flex items-center gap-2">
                  <Icon name="Eye" size={15} color="var(--color-primary)" />
                  <span className="text-sm font-caption font-600" style={{ color: "var(--color-foreground)" }}>Vista Previa</span>
                  <span className="text-xs font-caption hidden sm:inline" style={{ color: "var(--color-muted-foreground)" }}>— datos de muestra colombianos</span>
                </div>
                <div className="flex gap-1">
                  {["desktop", "mobile"]?.map((m) => (
                    <button
                      key={m}
                      onClick={() => setPreviewMode(m)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-caption transition-base"
                      style={{
                        backgroundColor: previewMode === m ? "var(--color-card)" : "transparent",
                        color: previewMode === m ? "var(--color-primary)" : "var(--color-muted-foreground)",
                        boxShadow: previewMode === m ? "var(--shadow-sm)" : "none",
                      }}
                    >
                      <Icon name={m === "desktop" ? "Monitor" : "Smartphone"} size={13} color="currentColor" />
                      {m === "desktop" ? "Escritorio" : "M\u00f3vil"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-4 flex justify-center" style={{ backgroundColor: "#e2e8f0" }}>
                <div
                  style={{
                    width: previewMode === "mobile" ? "375px" : "100%",
                    maxWidth: previewMode === "mobile" ? "375px" : "680px",
                    transition: "width 0.3s ease",
                  }}
                >
                  <div className="mb-2 px-3 py-2 rounded-lg text-xs font-caption" style={{ backgroundColor: "#fff", color: "#475569" }}>
                    <span className="font-600">Asunto: </span>{substituteVars(currentTemplate?.subject || "", SAMPLE_VARS)}
                    {currentTemplate?.preview_text && (
                      <span style={{ color: "#94a3b8" }}> — {substituteVars(currentTemplate?.preview_text, SAMPLE_VARS)}</span>
                    )}
                  </div>
                  <div
                    className="rounded-lg overflow-hidden shadow-md"
                    style={{ backgroundColor: "#fff" }}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Save button */}
          <div className="flex justify-end">
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges}
            >
              Guardar Cambios
            </Button>
          </div>
        </div>
      </div>
      {/* Reset confirm dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="rounded-2xl border p-6 max-w-sm w-full shadow-xl" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(239,68,68,0.1)" }}>
                <Icon name="RotateCcw" size={20} color="#ef4444" />
              </div>
              <div>
                <h3 className="font-heading font-700 text-base" style={{ color: "var(--color-foreground)" }}>Restaurar predeterminado</h3>
                <p className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>Esta acci\u00f3n no se puede deshacer</p>
              </div>
            </div>
            <p className="text-sm font-caption mb-5" style={{ color: "var(--color-muted-foreground)" }}>
              ¿Est\u00e1s seguro de que deseas restaurar la plantilla{" "}
              <strong style={{ color: "var(--color-foreground)" }}>\u201c{selectedDef?.name}\u201d</strong>{" "}
              a su valor predeterminado? Se perder\u00e1n todos los cambios personalizados.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowResetConfirm(false)}>Cancelar</Button>
              <Button variant="destructive" size="sm" iconName="RotateCcw" iconPosition="left" onClick={handleResetConfirm}>Restaurar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
